/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import _ from 'lodash';
import can from 'can';
import BaseWidget from 'scripts/base-widget';
import DomEventsManager from 'scripts/dom-events-manager';
import FeedService from 'scripts/services/base-feed-service';
import PlayerService from 'scripts/services/player-service';

import view from './qualifying-sectional-leaderboard.stache';
import ViewModel from './model';
import drawerView from './qualifying-sectional-leaderboard-drawer.stache';

const CUT_STATUSES = ['WD', 'MC', 'DQ'];

const defaultTimezone = window.globalSettings.defaultTimezone;

export const QualifyingSectionalLeaderboard = BaseWidget.extend({
    lazyLoadingDisabled: true,
    activeDrawer: null,

    init() {
        this.view = view;
        this.viewModel = new ViewModel(
            _.assign(
                {},
                {
                    favoritesIds: PlayerService.getQualifyingFavorites(),
                    defaultTimezone,
                },
                this.options.data,
            ),
        );
        this.fetchData();

        this._super();
        DomEventsManager.subscribe(this);
    },

    fetchData() {
        this.feedService = new FeedService(this.viewModel.feedUrl, false);
        this.feedService.fetch();
        this.feedService
            .on('fetching', (ev, value) =>
                this.viewModel.attr('fetching', value),
            )
            .on('data', (ev, data) => {
                can.batch.start();
                this.viewModel.attr('standings', _.get(data, 'standings', []));
                this.viewModel.attr(
                    'legend',
                    (data.fullLegend || []).join(' | '),
                );
                this.viewModel.attr(
                    'inlineLegend',
                    (data.inlineLegend || []).join(' | '),
                );
                this.viewModel.attr(
                    'currentRound',
                    _.get(data, 'currentRound.number', 1),
                );
                this.viewModel.attr('cutLine', data.cutLine);
                this.viewModel.attr('standings').setSortingHelpers();
                can.batch.stop();
                if (this.activeDrawer) {
                    const activeDrawerIdentifier = this.activeDrawer.attr(
                        'data-identifier',
                    );
                    const isFavoriteDrawer = !!this.activeDrawer.attr(
                        'data-is-favorite',
                    );
                    let playerRow = null;
                    if (isFavoriteDrawer) {
                        playerRow = this.element.find(
                            `.js-board-row[data-identifier="${activeDrawerIdentifier}"][data-is-favorite="true"]`,
                        );
                    } else {
                        playerRow = this.element.find(
                            `.js-board-row[data-identifier="${activeDrawerIdentifier}"][data-is-favorite!="true"]`,
                        );
                    }
                    if (!playerRow.length) {
                        this.removeDrawer();
                    } else {
                        playerRow.after(this.activeDrawer);
                        this.activeDrawer
                            .find('qualifying-leaderboard-drawer')
                            .viewModel()
                            .updateData(
                                this.getDrawerData(activeDrawerIdentifier),
                            );
                    }
                }
            });
    },

    getDrawerData(identifier) {
        return _.assign(
            {},
            {
                inlineLegend: this.viewModel.inlineLegend,
            },
            _.find(
                this.viewModel.standings,
                (standing) => standing.attr('player.identifier') === identifier,
            ),
        );
    },

    toggleDrawer($el) {
        let identifier = $el.data('identifier');
        let activeDraverIdentifier = null;
        if (this.activeDrawer) {
            activeDraverIdentifier = this.activeDrawer.attr('data-identifier');
            this.removeDrawer();
        }
        if (activeDraverIdentifier !== identifier) {
            let data = this.getDrawerData(identifier);
            if (data) {
                $el.after(drawerView(data));
                this.activeDrawer = $el.next();
                if ($el.attr('data-is-favorite')) {
                    this.activeDrawer.attr('data-is-favorite', true);
                }
            }
        }
    },

    removeDrawer() {
        this.activeDrawer.remove();
        this.activeDrawer = null;
    },

    // favorites
    '.icon-favorite-star click'(element, event) {
        event.stopPropagation();
        let playerId = element.data('identifier');
        PlayerService.toggleQualifyingFavorite(playerId.toString());

        // remove active favorite drawer
        if (this.activeDrawer) {
            const activeDrawerIdentifier = this.activeDrawer.attr(
                'data-identifier',
            );
            const isFavoriteDrawer = !!this.activeDrawer.attr(
                'data-is-favorite',
            );
            if (isFavoriteDrawer && activeDrawerIdentifier == playerId) {
                this.removeDrawer();
            }
        }
    },

    '.js-board-row:not(.is-drawer-disabled) click'(element) {
        this.toggleDrawer(element);
    },
});
